import {FormControlLabel, Radio, RadioGroup} from "@mui/material";
import {Consts} from "../pages/consts";
import {useContext, useState} from "react";
import AsyncSelect from "react-select/async";
import {checkDrugForDoubleEntry, extractDrugStrengthInfo, isSlovenia} from "../pages/utils";
import { strengthUnits } from "../../resources/json/jsons";
import {useTranslation} from "react-i18next";
import useMmcService from "../../services/MmcService";
import InitContext from "../pages/InitContext";

const SearchDrug = ({patDrugs, allProductNames, allSubstances, onSetDrug, onResetFields, onSetMessageText,
                      onSetStrength, onSetStrengthUnit, language}) => {
  const { t } = useTranslation();
  const context = useContext(InitContext);
  const [drugSearchKind, setDrugSearchKind] = useState(Consts.PRODUCT);
  const [selectedValue, setSelectedValue] = useState({ value: "", label: "" });
  const { clearError, getDrugByProductNameJson } = useMmcService();

  const loadOptions = (inputValue, callback) => {
    setTimeout(() => {
      const options = Array.from({ length: 33000 }, (_, i) => ({
        value:
          drugSearchKind === Consts.PRODUCT
            ? allProductNames[i]
            : allSubstances[i],
        label:
          drugSearchKind === Consts.PRODUCT
            ? allProductNames[i]
            : allSubstances[i]?.replace("##", ": ")?.replaceAll('&&&', ` ${t("and")} `),
      }))?.filter((option) =>
        option?.label?.toLowerCase()?.startsWith(inputValue?.toLowerCase()),
      );

      const allOptions = options?.length > 450 ? [...options?.slice(0, 450), ...options?.slice(450)] : options;
      callback(allOptions?.slice(0, 500));
    }, 500);
  };

  function handleChangingSearchKind(e) {
    onSetMessageText("");
    setSelectedValue({ value: "", label: "" });
    setDrugSearchKind(e.target.value);
  }

  const resetFields = () => {
    onSetMessageText("");
    onSetDrug({});
    onResetFields();
  }

  const handleSelectDrug = (drug) => {
    resetFields();
    let productName = drug?.value;
    let alreadyExists = false;

    let labelForSelect = drug?.value?.indexOf('##') >= 0
      ? drug?.value?.replace("##", ', ')?.replaceAll('&&&', ` ${t("and")} `)
      : drug?.value;

    setSelectedValue({ label: labelForSelect, value: drug?.value });

    if (drugSearchKind === Consts.SUBSTANCE) {
      const parts = drug?.value?.split('##');
      productName = parts[1];
      alreadyExists = checkDrugForDoubleEntry(patDrugs, labelForSelect);

      if (alreadyExists) {
        onSetMessageText(t("drug_already_added"));
        setSelectedValue({ value: "", label: "" });
      } else {
        onSetMessageText("");
      }
    }

    if(!alreadyExists){
      clearError();

      getDrugByProductNameJson(context?.token, productName, language)
        .then((data) => {
          // activeSubstance in patDrugs are strings, in data (new drug) is a string
          alreadyExists = checkDrugForDoubleEntry(patDrugs, data?.productName);

          if (alreadyExists) {
            onSetMessageText(t("drug_already_added"));
          } else {
            onSetMessageText("");
            const [strength, strengthUnit] = extractDrugStrengthInfo(productName);
            onSetStrength(strength);
            onSetStrengthUnit(strengthUnit);

            onSetDrug({
              ...data,
              strength: strength,
              strengthUnit: strengthUnits?.find(
                (option) => option?.value === strengthUnit,
              )?.label,
              frequency: 1,
              frequencyUnit: "day(s)",
              timingType: Consts.DAY_TIMES,
              status: Consts.ACTIVE,
              intakes: Consts.DEFAULT_DAY_TIME_INTAKES
            });
          }
        })
        .catch((message) => onSetMessageText(message));
    }
  };

  return (
    <div className="w3-row w3-padding-small"  style={{ display: 'flex', alignItems: 'center' }}>
      <div className="w3-col s5 w3-mobile">
        <RadioGroup
          row
          aria-labelledby="product-substance-group"
          name="product-substance-group"
          value={drugSearchKind}
          defaultValue={drugSearchKind}
          onChange={handleChangingSearchKind}
        >
          <FormControlLabel
            value={Consts.SUBSTANCE}
            control={<Radio />}
            label={<label>{t("active_substance")}</label>}
          />
          <FormControlLabel
            value={Consts.PRODUCT}
            control={<Radio />}
            label={<label>{t("product_name")}</label>}
          />
        </RadioGroup>
      </div>
      <div className="w3-col s5 w3-mobile">
        <AsyncSelect
          key={drugSearchKind} // triggers re-rendering
          id="drug"
          cacheOptions
          loadOptions={loadOptions}
          onChange={handleSelectDrug}
          defaultOptions
          placeholder={t("select")}
          className="custom-select"
          classNamePrefix="custom-select"
          value={selectedValue}
        />
      </div>
    </div>
  );
}

export default SearchDrug;